import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Oasis from "../../../../../assets/Oasis-white.svg";
import { logoutUser } from "../../../../pages/login/functions";
import { useUserStore } from "../../../../store/userStore";
import tw from "tailwind-styled-components";

interface NavbarProps {
  onOpenAppsSidebar?: () => void;
  isDarkMode?: boolean;
  onClose?: () => void;
  setShowProfileMenu?: React.Dispatch<React.SetStateAction<boolean>>;
}

const themeClasses = (isDarkMode: boolean) => ({
  bg: isDarkMode ? "bg-dark-surface" : "bg-white",
  border: isDarkMode ? "border-dark-border" : "border-gray-200",
  text: isDarkMode ? "text-white" : "text-gray-900",
  textSecondary: isDarkMode ? "text-gray-400" : "text-gray-500",
  cardBg: isDarkMode ? "bg-dark-card" : "bg-white",
  cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
  buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-50",
  buttonText: isDarkMode ? "text-gray-400" : "text-gray-500",
  buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
  inputBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
  inputBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
  inputText: isDarkMode ? "text-white" : "text-gray-900",
  inputPlaceholder: isDarkMode
    ? "placeholder-gray-500"
    : "placeholder-gray-500",
});

function Navbar({
  onOpenAppsSidebar,
  isDarkMode = false,
}: NavbarProps) {
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Función para manejar el estado exclusivo de los menús
  const handleMenuToggle = (menu: "notifications" | "profile") => {
    // Cerrar todos los menús primero
    setShowNotifications(false);
    setShowProfile(false);

    // Abrir solo el menú seleccionado si no estaba ya abierto
    switch (menu) {
      case "notifications":
        setShowNotifications(!showNotifications);
        break;
      case "profile":
        setShowProfile(!showProfile);
        break;
    }
  };

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;

      // Check if click is outside any menu
      if (!target.closest("[data-menu]")) {
        setShowNotifications(false);
        setShowProfile(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const tc = themeClasses(isDarkMode);
  const { t } = useTranslation("common");
  const user = useUserStore((state) => state.user);

  return (
    <Nav
      className={`${tc.bg} border-b ${tc.border} px-3 py-3 lg:px-5 lg:pl-3 shadow-sm`}
    >
      <Container>
        <div className="flex items-center space-x-3">
          <div
            className={`w-8 h-8 bg-gradient-to-br ${
              isDarkMode
                ? "from-[#1C93FF] to-[#2B2B2B]"
                : "from-[#1C93FF] to-[#197CD6]"
            } rounded-lg flex items-center justify-center shadow-lg`}
          >
            {/* <i className="ri-navigation-line text-white text-sm"></i> */}
            <img src={Oasis} alt="Oasis" className="w-10 h-10" />
          </div>
          <div>
            <h1 className={`text-lg font-bold ${tc.text}`}>
              {t("header.patioManiobras")}
            </h1>
            <p className={`text-xs ${tc.textSecondary}`}>{t("header.dashboard")}</p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-2">
          {/* Notifications */}
          <div className="relative">
            <button
              onClick={() => handleMenuToggle("notifications")}
              data-menu="notifications"
              className={`p-2 ${tc.buttonText} hover:text-[#1C93FF] ${
                isDarkMode ? "hover:bg-[#1C93FF]/10" : "hover:bg-[#1C93FF]/10"
              } rounded-lg transition-all relative`}
            >
              <i className="ri-notification-line text-lg"></i>
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                3
              </span>
            </button>
            {showNotifications && (
              <div
                data-menu="notifications"
                className={`absolute right-0 mt-2 w-80 ${tc.cardBg} border ${tc.cardBorder} rounded-xl shadow-xl z-50 overflow-hidden`}
              >
                <div
                  className={`p-3 ${
                    isDarkMode ? "bg-dark-card" : "bg-gray-50"
                  } border-b ${tc.cardBorder}`}
                >
                  <div className="flex items-center justify-between">
                    <h3 className={`font-semibold ${tc.text} text-sm`}>
                      {t("notifications.title")}
                    </h3>
                    <button className="text-xs text-[#1C93FF] hover:text-[#197CD6] font-medium">
                      {t("notifications.markAllRead")}
                    </button>
                  </div>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  <div
                    className={`p-3 ${tc.buttonHover} border-b ${tc.cardBorder} transition-colors`}
                  >
                    <div className="flex items-start space-x-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full mt-1.5 flex-shrink-0"></div>
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${tc.text}`}>
                          Batería baja en DM-58 SUB780A
                        </p>
                        <p className={`text-xs ${tc.textSecondary} mt-1`}>
                          El equipo requiere carga inmediata
                        </p>
                        <p className={`text-xs ${tc.textSecondary} mt-1`}>
                          Hace 5 minutos
                        </p>
                      </div>
                    </div>
                  </div>
                  <div
                    className={`p-3 ${tc.buttonHover} border-b ${tc.cardBorder} transition-colors`}
                  >
                    <div className="flex items-start space-x-2">
                      <div className="w-2 h-2 bg-amber-500 rounded-full mt-1.5 flex-shrink-0"></div>
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${tc.text}`}>
                          Velocidad excedida
                        </p>
                        <p className={`text-xs ${tc.textSecondary} mt-1`}>
                          Eco 4 Light+ superó el límite permitido
                        </p>
                        <p className={`text-xs ${tc.textSecondary} mt-1`}>
                          Hace 12 minutos
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className={`p-3 ${tc.buttonHover} transition-colors`}>
                    <div className="flex items-start space-x-2">
                      <div className="w-2 h-2 bg-[#1C93FF] rounded-full mt-1.5 flex-shrink-0"></div>
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${tc.text}`}>
                          Nuevo equipo conectado
                        </p>
                        <p className={`text-xs ${tc.textSecondary} mt-1`}>
                          Mazda CX-9 se conectó exitosamente
                        </p>
                        <p className={`text-xs ${tc.textSecondary} mt-1`}>
                          Hace 1 hora
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* Profile */}
          <div className="relative">
            <button
              onClick={() => handleMenuToggle("profile")}
              data-menu="profile"
              className={`flex items-center space-x-2 p-1.5 ${tc.buttonHover} rounded-lg transition-all`}
            >
              <div
                className={`w-7 h-7 bg-gradient-to-br ${
                  isDarkMode
                    ? "from-[#1C93FF] to-[#2B2B2B]"
                    : "from-[#1C93FF] to-[#197CD6]"
                } rounded-lg flex items-center justify-center shadow-sm`}
              >
                <span className="text-white text-xs font-semibold">
                  {user && user.first_name && user.last_name
                    ? `${user.first_name.charAt(0).toUpperCase()}${user.last_name
                        .charAt(0)
                        .toUpperCase()}`
                    : "DU"}
                </span>
              </div>
              <div className="text-left hidden sm:block">
                <p className={`text-sm font-medium ${tc.text}`}>
                  {user && user.firstName && user.lastName
                    ? `${user.firstName} ${user.lastName}`
                    : "Utilitari"}
                </p>
                <p className={`text-xs ${tc.textSecondary}`}>{t("profileMenu.admin")}</p>
              </div>
              <i className={`ri-arrow-down-s-line ${tc.buttonText} text-sm`}></i>
            </button>
            {showProfile && (
              <div
                data-menu="profile"
                className={`absolute right-0 mt-2 w-52 ${tc.cardBg} border ${tc.cardBorder} rounded-xl shadow-xl z-50 overflow-hidden`}
              >
                <div
                  className={`p-3 ${
                    isDarkMode ? "bg-dark-card" : "bg-gray-50"
                  } border-b ${tc.cardBorder}`}
                >
                  <div className="flex items-center space-x-2">
                    <div
                      className={`w-10 h-10 bg-gradient-to-br ${
                        isDarkMode
                          ? "from-[#1C93FF] to-[#2B2B2B]"
                          : "from-[#1C93FF] to-[#197CD6]"
                      } rounded-lg flex items-center justify-center`}
                    >
                      <span className="text-white font-semibold text-sm">
                        {user && user.firstName && user.lastName
                          ? `${user.firstName.charAt(0).toUpperCase()}${user.lastName.charAt(0).toUpperCase()}`
                          : "DU"}
                      </span>
                    </div>
                    <div>
                      <p className={`font-medium ${tc.text} text-sm`}>
                        Utilitari
                      </p>
                      <p className={`text-xs ${tc.textSecondary}`}>
                        admin@company.com
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-1.5">
                  <button
                    className={`w-full text-left px-2.5 py-2 text-sm ${tc.buttonText} ${tc.buttonTextHover} ${tc.buttonHover} rounded-lg transition-colors flex items-center space-x-2`}
                  >
                    <i className={`ri-user-line ${tc.buttonText} text-sm`}></i>
                    <span>{t("profileMenu.myProfile")}</span>
                  </button>
                  <button
                    className={`w-full text-left px-2.5 py-2 text-sm ${tc.buttonText} ${tc.buttonTextHover} ${tc.buttonHover} rounded-lg transition-colors flex items-center space-x-2`}
                  >
                    <i
                      className={`ri-settings-line ${tc.buttonText} text-sm`}
                    ></i>
                    <span>{t("profileMenu.settings")}</span>
                  </button>
                  <button
                    className={`w-full text-left px-2.5 py-2 text-sm ${tc.buttonText} ${tc.buttonTextHover} ${tc.buttonHover} rounded-lg transition-colors flex items-center space-x-2`}
                  >
                    <i
                      className={`ri-question-line ${tc.buttonText} text-sm`}
                    ></i>
                    <span>{t("profileMenu.helpSupport")}</span>
                  </button>
                  <hr className={`my-1.5 border ${tc.cardBorder}`} />
                  <button
                    onClick={async () => {
                      setIsLoggingOut(true);
                      await logoutUser();
                      setIsLoggingOut(false);
                    }}
                    className="w-full text-left px-2.5 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors flex items-center space-x-2"
                  >
                    {isLoggingOut ? (
                      <>
                        <svg
                          className="animate-spin h-4 w-4 text-red-600"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        <span>{t("profileMenu.loggingOut")}</span>
                      </>
                    ) : (
                      <>
                        <i className="ri-logout-box-line text-sm"></i>
                        <span>{t("profileMenu.logout")}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
          {/* Admin Panel Button */}
          <button
            aria-label={t("general.openApps")}
            onClick={onOpenAppsSidebar}
            className={`p-2 ${tc.buttonText} ${tc.buttonTextHover} ${tc.buttonHover} rounded-lg transition-all border ${tc.cardBorder}`}
          >
            <i className="ri-dashboard-line text-lg"></i>
          </button>
        </div>
      </Container>
    </Nav>
  );
}

const Nav = tw.nav`
w-full
`;
const Container = tw.div`
flex items-center justify-between
`;

export default Navbar;
